import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
  FileSpreadsheet, X, Loader2, Sparkles, Clock, CheckCircle, Database, Lock, Zap,
  Crown, Brain, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost, apiFetch, ApiError } from '../../lib/apiClient';

export default function ErpTab({ user, setUser, setActiveTab }) {
  const navigate = useNavigate();
  const isFreeTier = user?.subscription_tier === 'free';

  // ERP Smart Import state
  const [erpFile, setErpFile] = useState(null);
  const [erpPreview, setErpPreview] = useState(null);
  const [erpDetection, setErpDetection] = useState(null);
  const [erpLoading, setErpLoading] = useState(false);
  const [erpDetecting, setErpDetecting] = useState(false);
  const [erpHistory, setErpHistory] = useState([]);
  const [erpHistoryLoading, setErpHistoryLoading] = useState(false);

  // Fetch ERP history on mount (tab is now standalone; no need to gate on activeTab)
  useEffect(() => {
    const fetchErpHistory = async () => {
      setErpHistoryLoading(true);
      try {
        const data = await apiGet('/api/erp/history');
        setErpHistory(data?.history || []);
      } catch (e) {
        console.error('Failed to fetch ERP history:', e);
      }
      setErpHistoryLoading(false);
    };
    fetchErpHistory();
  }, []);

  const handleErpFileChange = async (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const validExtensions = ['.csv', '.xlsx', '.xls'];
      const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      
      if (!hasValidExtension) {
        toast.error('Please upload a CSV or Excel file');
        return;
      }
      
      setErpFile(file);
      setErpDetection(null);
      setErpPreview(null);
      
      // Auto-detect ERP format
      await detectErpFormat(file);
    }
  };

  const detectErpFormat = async (file) => {
    setErpDetecting(true);

    // Check file size (warn if > 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.warning('Large file detected. Upload may take longer...');
    }

    const formData = new FormData();
    formData.append('file', file);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout

    try {
      const data = await apiFetch('/api/analyze/erp-detect', {
        method: 'POST',
        body: formData,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      setErpDetection(data);
      setErpPreview(data?.preview || []);
      if (data?.detected_format !== 'UNKNOWN') {
        toast.success(`Detected ${data.detected_format} format with ${data.confidence * 100}% confidence`);
      } else {
        toast.warning('Could not auto-detect ERP format. Standard column mapping will be used.');
      }
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('ERP detect error:', error);
      if (error.name === 'AbortError') {
        toast.error('Upload timed out. Please try a smaller file or check your connection.');
      } else if (error instanceof ApiError) {
        toast.error(error.message || `Upload failed`);
      } else {
        toast.error('Connection error. Please check your internet connection and try again.');
      }
      // Clear the file selection on error
      setErpFile(null);
    } finally {
      setErpDetecting(false);
    }
  };


  const handleErpImport = async () => {
    if (!erpFile) {
      toast.error('Please select a file first');
      return;
    }

    setErpLoading(true);

    const formData = new FormData();
    formData.append('file', erpFile);
    if (erpDetection?.detected_format) {
      formData.append('format_type', erpDetection.detected_format);
    }

    try {
      // Step 1: Upload file
      const uploadData = await apiPost('/api/analyze/erp-import', formData);

      // Check if it was processed synchronously (small file)
      if (uploadData.analysis_id) {
        toast.success(`ERP Import complete! ${uploadData.total_skus} products processed.`);
        navigate(`/analysis/${uploadData.analysis_id}`);
        setErpLoading(false);
        return;
      }

      // Step 2: Start processing (for large files)
      const jobId = uploadData.job_id;
      toast.success(`Processing ${uploadData.total_products} products...`);

      try {
        await apiPost(`/api/erp/process/${jobId}`);
      } catch (procErr) {
        const msg = procErr instanceof ApiError ? (procErr.message || 'Failed to start processing') : 'Failed to start processing';
        toast.error(msg);
        setErpLoading(false);
        return;
      }

      // Step 3: Poll for status
      const pollStatus = async () => {
        try {
          const statusData = await apiGet(`/api/erp/status/${jobId}`);

          if (statusData.status === 'complete') {
            toast.success(`ERP Import complete! ${statusData.total_products} products processed.`);
            navigate(`/analysis/${statusData.analysis_id}`);
            setErpLoading(false);
            return true;
          } else if (statusData.status === 'failed') {
            toast.error(statusData.error || 'ERP processing failed');
            setErpLoading(false);
            return true;
          } else {
            // Still processing - show progress
            const progress = statusData.progress_percent || 0;
            if (progress > 0) {
              toast.loading(`Processing: ${progress}% (${statusData.processed}/${statusData.total_products})`, { id: 'erp-progress' });
            }
            return false;
          }
        } catch (error) {
          console.error('Status poll error:', error);
          return false;
        }
      };

      // Poll every 3 seconds until complete
      const pollInterval = setInterval(async () => {
        const done = await pollStatus();
        if (done) {
          clearInterval(pollInterval);
          toast.dismiss('erp-progress');
        }
      }, 3000);

      // Initial poll
      await pollStatus();

    } catch (error) {
      const msg = error instanceof ApiError ? (error.message || 'ERP Upload failed') : 'Connection error. Please try again.';
      toast.error(msg);
      setErpLoading(false);
    }
  };

  const clearErpFile = () => {
    setErpFile(null);
    setErpDetection(null);
    setErpPreview(null);
  };

  // Financial Impact Analyzer handlers

  return (
          /* ERP Smart Import Tab - Premium */
          <div data-testid="erp-tab-content">
            {/* Premium Hero Header */}
            <div className="relative mb-8 rounded-2xl overflow-hidden bg-gradient-to-br from-[#1e1b4b] via-[#312e81] to-[#4c1d95] p-8">
              <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 20% 50%, #8B5CF6 0%, transparent 50%), radial-gradient(circle at 80% 20%, #EC4899 0%, transparent 40%)'}} />
              <div className="relative z-10 flex items-center justify-between">
                <div className="flex items-center gap-5">
                  <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20 shadow-xl">
                    <Database className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h1 className="font-outfit text-3xl font-bold text-white">ERP Smart Import</h1>
                      <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#F59E0B] to-[#EF4444] text-white shadow-lg">Premium</span>
                    </div>
                    <p className="text-indigo-200 font-jakarta">Import from SAP Material Master, BOMs & enterprise ERP systems</p>
                  </div>
                </div>
                <div className="hidden lg:flex items-center gap-4">
                  <div className="text-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-xl border border-white/10">
                    <p className="text-xs text-indigo-300">Credit Cost</p>
                    <p className="text-lg font-bold text-white">5x</p>
                  </div>
                  <div className="text-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-xl border border-white/10">
                    <p className="text-xs text-indigo-300">Formats</p>
                    <p className="text-lg font-bold text-white">SAP</p>
                  </div>
                </div>
              </div>
            </div>

            {isFreeTier ? (
              /* Locked State for Free Users */
              <div className="card-modern p-8 relative overflow-hidden">
                <div className="text-center max-w-md mx-auto py-12">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#8B5CF6]/10 to-[#EC4899]/10 flex items-center justify-center mx-auto mb-6">
                    <Lock className="w-10 h-10 text-[#8B5CF6]" />
                  </div>
                  <h3 className="font-outfit font-bold text-2xl text-slate-900 mb-3">Unlock ERP Smart Import</h3>
                  <p className="text-slate-500 mb-6 leading-relaxed">
                    Available on Starter plan and above. Automatically detect SAP export formats, map columns intelligently, and classify all your enterprise products at once.
                  </p>
                  <div className="grid grid-cols-3 gap-3 mb-6">
                    <div className="bg-[#8B5CF6]/5 rounded-xl p-4">
                      <Database className="w-5 h-5 text-[#8B5CF6] mx-auto mb-2" />
                      <p className="text-xs font-semibold text-[#8B5CF6]">Auto-Detect</p>
                      <p className="text-xs text-slate-500">SAP formats</p>
                    </div>
                    <div className="bg-[#EC4899]/5 rounded-xl p-4">
                      <Sparkles className="w-5 h-5 text-[#EC4899] mx-auto mb-2" />
                      <p className="text-xs font-semibold text-[#EC4899]">Smart Mapping</p>
                      <p className="text-xs text-slate-500">Column recognition</p>
                    </div>
                    <div className="bg-[#F59E0B]/5 rounded-xl p-4">
                      <Crown className="w-5 h-5 text-[#F59E0B] mx-auto mb-2" />
                      <p className="text-xs font-semibold text-[#F59E0B]">5x Credits</p>
                      <p className="text-xs text-slate-500">Premium processing</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate('/billing')}
                    className="bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white hover:opacity-90 px-8 py-5 text-base"
                    data-testid="erp-upgrade-btn"
                  >
                    <Zap className="w-5 h-5 mr-2" />
                    Upgrade to Unlock
                  </Button>
                </div>
              </div>
            ) : (
              /* Active State for Paid Users */
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Left: Upload Panel */}
                <div className="lg:col-span-2 card-modern p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] flex items-center justify-center shadow-lg">
                      <Database className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="font-outfit text-lg font-semibold text-slate-900">Upload ERP Export</h2>
                      <p className="text-sm text-slate-500 font-jakarta">SAP Material Master, BOM exports (CSV, XLSX)</p>
                    </div>
                  </div>

                  {/* AI Enhancement Banner */}
                  <div className="flex items-center gap-2 px-3 py-2 mb-5 bg-gradient-to-r from-[#6366F1]/10 to-[#8B5CF6]/10 rounded-xl border border-[#6366F1]/20">
                    <Sparkles className="w-4 h-4 text-[#6366F1]" />
                    <span className="text-xs text-[#6366F1] font-medium">AI-Powered</span>
                    <span className="text-xs text-slate-600">AI improvement tools available on classification results</span>
                  </div>

                  {/* Upload Zone */}
                  <div className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all mb-5 ${
                    erpFile ? 'border-[#8B5CF6] bg-[#8B5CF6]/5' : 'border-slate-200 hover:border-[#8B5CF6]/50'
                  }`}>
                    {erpFile ? (
                      <div className="space-y-4">
                        <div className="flex items-center justify-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] flex items-center justify-center">
                            <FileSpreadsheet className="w-5 h-5 text-white" />
                          </div>
                          <div className="text-left">
                            <p className="font-medium text-slate-900 text-sm">{erpFile.name}</p>
                            <p className="text-xs text-slate-500">{(erpFile.size / 1024).toFixed(1)} KB</p>
                          </div>
                          <button
                            onClick={clearErpFile}
                            className="ml-2 p-1.5 hover:bg-slate-100 rounded-lg"
                            data-testid="clear-erp-file-btn"
                          >
                            <X className="w-4 h-4 text-slate-400" />
                          </button>
                        </div>

                        {/* Detection Results */}
                        {erpDetecting && (
                          <div className="flex items-center justify-center gap-2 text-sm text-[#8B5CF6]">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Detecting ERP format...
                          </div>
                        )}

                        {erpDetection && !erpDetecting && (
                          <div className="bg-white rounded-xl p-4 text-left border border-[#8B5CF6]/20">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-sm font-medium text-slate-700">Format Detected</span>
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                erpDetection.detected_format !== 'UNKNOWN'
                                  ? 'bg-[#10B981]/10 text-[#10B981]'
                                  : 'bg-[#F59E0B]/10 text-[#F59E0B]'
                              }`}>
                                {erpDetection.detected_format !== 'UNKNOWN'
                                  ? erpDetection.detected_format.replace('_', ' ')
                                  : 'Standard Format'}
                              </span>
                            </div>

                            {erpDetection.detected_columns?.length > 0 && (
                              <div className="mb-3">
                                <p className="text-xs text-slate-500 mb-1">Mapped columns:</p>
                                <div className="flex flex-wrap gap-1">
                                  {erpDetection.detected_columns.slice(0, 8).map((col, i) => (
                                    <span key={i} className="px-2 py-0.5 bg-[#8B5CF6]/10 text-[#8B5CF6] rounded text-xs">
                                      {col}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className="flex items-center justify-between text-xs">
                              <span className="text-slate-500">{erpDetection.total_rows} products found</span>
                              <span className="text-[#F59E0B] font-medium">
                                {erpDetection.credits_required} ERP credits required
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Preview Table */}
                        {erpPreview && erpPreview.length > 0 && (
                          <div className="bg-slate-50 rounded-xl p-3 max-h-[150px] overflow-auto text-left">
                            <p className="text-xs text-slate-500 mb-2">Preview:</p>
                            <div className="space-y-1">
                              {erpPreview.slice(0, 5).map((row, i) => (
                                <div key={i} className="text-xs text-slate-600 truncate">
                                  {row.description || row.sku_id || 'Row ' + (i + 1)}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* ERP File Quality Summary */}
                        {erpDetection?.quality_stats && (
                          <div className="bg-gradient-to-r from-[#6366F1]/5 to-[#8B5CF6]/5 rounded-xl p-4 border border-[#6366F1]/20">
                            <div className="flex items-center gap-2 mb-3">
                              <Sparkles className="w-4 h-4 text-[#6366F1]" />
                              <span className="text-sm font-medium text-slate-700">Data Quality Analysis</span>
                            </div>

                            <div className="grid grid-cols-4 gap-2 mb-3">
                              <div className="text-center p-2 bg-white rounded-lg">
                                <p className="text-lg font-bold text-slate-900">{erpDetection.quality_stats.total}</p>
                                <p className="text-xs text-slate-500">Total</p>
                              </div>
                              <div className="text-center p-2 bg-green-50 rounded-lg">
                                <p className="text-lg font-bold text-green-600">{erpDetection.quality_stats.good}</p>
                                <p className="text-xs text-green-600">Good</p>
                              </div>
                              <div className="text-center p-2 bg-amber-50 rounded-lg">
                                <p className="text-lg font-bold text-amber-600">{erpDetection.quality_stats.fair}</p>
                                <p className="text-xs text-amber-600">Fair</p>
                              </div>
                              <div className="text-center p-2 bg-red-50 rounded-lg">
                                <p className="text-lg font-bold text-red-500">{erpDetection.quality_stats.poor}</p>
                                <p className="text-xs text-red-500">Brief</p>
                              </div>
                            </div>

                            {(erpDetection.quality_stats.fair + erpDetection.quality_stats.poor) > 0 && (
                              <p className="text-xs text-slate-500">
                                {erpDetection.quality_stats.fair + erpDetection.quality_stats.poor} descriptions could benefit from more detail. AI improvement tools will be available on the results page after analysis.
                              </p>
                            )}
                            {erpDetection.quality_stats.missing_hs > 0 && (
                              <p className="text-xs text-slate-500 mt-1">
                                {erpDetection.quality_stats.missing_hs} rows missing HS codes — AI suggestions available after classification.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#8B5CF6]/20 to-[#EC4899]/20 flex items-center justify-center mx-auto mb-4">
                          <Database className="w-7 h-7 text-[#8B5CF6]" />
                        </div>
                        <p className="text-slate-600 text-sm mb-1">
                          <label className="text-[#8B5CF6] font-semibold cursor-pointer hover:underline">
                            Upload ERP Export
                            <input
                              type="file"
                              className="hidden"
                              accept=".csv,.xlsx,.xls"
                              onChange={handleErpFileChange}
                              data-testid="erp-file-input"
                            />
                          </label>
                        </p>
                        <p className="text-xs text-slate-400">SAP Material Master, BOM exports (CSV, XLSX)</p>
                      </>
                    )}
                  </div>

                  {/* Import Button */}
                  <Button
                    onClick={handleErpImport}
                    className="w-full bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white py-5 hover:opacity-90 text-base"
                    disabled={!erpFile || erpLoading || erpDetecting}
                    data-testid="erp-import-btn"
                  >
                    {erpLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Processing ERP Data...
                      </>
                    ) : (
                      <>
                        <Database className="w-5 h-5 mr-2" />
                        Import & Classify
                      </>
                    )}
                  </Button>
                </div>

                {/* Right: Info Panel */}
                <div className="space-y-5">
                  {/* Capabilities */}
                  <div className="card-modern p-5">
                    <h3 className="font-outfit font-semibold text-sm text-slate-900 mb-4">Capabilities</h3>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-[#8B5CF6]/10 flex items-center justify-center flex-shrink-0">
                          <Database className="w-4 h-4 text-[#8B5CF6]" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">Auto-Detection</p>
                          <p className="text-xs text-slate-500">Identifies SAP Material Master & BOM formats</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-[#EC4899]/10 flex items-center justify-center flex-shrink-0">
                          <Sparkles className="w-4 h-4 text-[#EC4899]" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">Smart Column Mapping</p>
                          <p className="text-xs text-slate-500">MATNR, MAKTX, HERKL, STAWN auto-mapped</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center flex-shrink-0">
                          <Brain className="w-4 h-4 text-[#F59E0B]" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">AI Enhancement</p>
                          <p className="text-xs text-slate-500">Description improvement on results page</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Supported Formats */}
                  <div className="card-modern p-5">
                    <h3 className="font-outfit font-semibold text-sm text-slate-900 mb-3">Supported Formats</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
                        <CheckCircle className="w-4 h-4 text-[#10B981]" />
                        <span className="text-xs text-slate-700">SAP Material Master (MM03)</span>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
                        <CheckCircle className="w-4 h-4 text-[#10B981]" />
                        <span className="text-xs text-slate-700">SAP BOM Exports</span>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
                        <CheckCircle className="w-4 h-4 text-[#10B981]" />
                        <span className="text-xs text-slate-700">Custom CSV/XLSX with standard columns</span>
                      </div>
                    </div>
                  </div>

                  {/* Credit Info */}
                  <div className="bg-gradient-to-br from-[#8B5CF6]/5 to-[#EC4899]/5 rounded-xl p-5 border border-[#8B5CF6]/10">
                    <div className="flex items-center gap-2 mb-2">
                      <Crown className="w-4 h-4 text-[#F59E0B]" />
                      <span className="text-sm font-semibold text-slate-800">Premium Processing</span>
                    </div>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      ERP imports consume 5x credits per product due to enhanced column mapping and format detection. AI improvement tools are available on the results page.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ERP Import History Panel */}
            <div className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B5CF6]/10 to-[#EC4899]/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-[#8B5CF6]" />
                  </div>
                  <div>
                    <h3 className="font-outfit font-semibold text-slate-900">Import History</h3>
                    <p className="text-xs text-slate-500">Past ERP imports and their results</p>
                  </div>
                </div>
              </div>

              {erpHistoryLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#8B5CF6]" />
                </div>
              ) : erpHistory.length === 0 ? (
                <div className="card-modern p-6 text-center">
                  <Database className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No ERP imports yet. Upload your first file above to get started.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {erpHistory.map((item) => (
                    <div
                      key={item.analysis_id}
                      className="card-modern p-4 hover:border-[#8B5CF6]/30 transition-all cursor-pointer group"
                      onClick={() => navigate(`/analysis/${item.analysis_id}`)}
                      data-testid={`erp-history-${item.analysis_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] flex items-center justify-center flex-shrink-0 shadow-sm">
                            <Database className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-semibold text-sm text-slate-900">
                                {item.file_name || item.erp_format || 'ERP Import'}
                              </p>
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-[#8B5CF6]/10 text-[#8B5CF6]">
                                {item.erp_format || 'CSV'}
                              </span>
                            </div>
                            <p className="text-xs text-slate-500">
                              {new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-center">
                            <p className="text-lg font-bold text-slate-900">{item.total_skus}</p>
                            <p className="text-[10px] text-slate-500 uppercase">Products</p>
                          </div>
                          {item.compliance_issues > 0 && (
                            <div className="text-center">
                              <p className="text-lg font-bold text-[#EF4444]">{item.compliance_issues}</p>
                              <p className="text-[10px] text-[#EF4444] uppercase">Issues</p>
                            </div>
                          )}
                          {item.total_estimated_savings > 0 && (
                            <div className="text-center">
                              <p className="text-lg font-bold text-[#10B981]">${Math.round(item.total_estimated_savings / 1000)}K</p>
                              <p className="text-[10px] text-[#10B981] uppercase">Savings</p>
                            </div>
                          )}
                          {item.compliance_exposure > 0 && (
                            <div className="text-center">
                              <p className="text-lg font-bold text-[#F59E0B]">${Math.round(item.compliance_exposure / 1000)}K</p>
                              <p className="text-[10px] text-[#F59E0B] uppercase">Exposure</p>
                            </div>
                          )}
                          <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-[#8B5CF6] transition-colors" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
  );
}
